import React, { useEffect, useState, useMemo } from 'react';
import {
  Stack,
  Text,
  SearchBox,
  CommandBar,
  ICommandBarItemProps,
  DetailsList,
  DetailsListLayoutMode,
  Selection,
  SelectionMode,
  IColumn,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Spinner,
  MessageBar,
  MessageBarType,
  Modal,
  TextField,
  Dropdown,
  IDropdownOption,
} from '@fluentui/react';
import { IArtikel, IUserPermissions, IMengeneinheit } from '../../shared/models';
import { artikelApi, mengeneinheitenApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { centeredModalStyles, modalContentStyles } from '../styles/dialogs';
import { ArtikelAnlegenDialog } from '../components/ArtikelAnlegenDialog';
import axios from 'axios';

export const ArtikelListe: React.FC = () => {
  const { user } = useAuth();
  const [artikel, setArtikel] = useState<IArtikel[]>([]);
  const [filteredArtikel, setFilteredArtikel] = useState<IArtikel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedItems, setSelectedItems] = useState<IArtikel[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [permissions, setPermissions] = useState<IUserPermissions | null>(null);

  // Dialog States
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedArtikel, setSelectedArtikel] = useState<IArtikel | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<IArtikel>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mengeneinheiten, setMengeneinheiten] = useState<IMengeneinheit[]>([]);
  const [einzelpreisInput, setEinzelpreisInput] = useState<string>('0');

  useEffect(() => {
    loadArtikel();
    loadPermissions();
  }, []);

  useEffect(() => {
    filterArtikel();
  }, [searchText, artikel]);

  const loadPermissions = async () => {
    try {
      if (user?.email) {
        const response = await axios.get('/api/permissions/me', {
          params: { email: user.email }
        });
        setPermissions(response.data);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden der Berechtigungen:', err);
    }
  };

  const loadMengeneinheiten = async () => {
    try {
      const data = await mengeneinheitenApi.getAll();
      setMengeneinheiten(data);
    } catch (err) {
      console.error('Fehler beim Laden der Mengeneinheiten:', err);
    }
  };

  const loadArtikel = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await artikelApi.getAll();
      setArtikel(data);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Artikel');
    } finally {
      setLoading(false);
    }
  };

  const filterArtikel = () => {
    let filtered = [...artikel];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((a) =>
        a.Artikelname?.toLowerCase().includes(searchLower) ||
        a.Artikelnummer?.toLowerCase().includes(searchLower) ||
        a.BCNummer?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredArtikel(filtered);
  };

  const parseDecimal = (value: string): number => {
    if (!value || value.trim() === '') return 0;
    const normalized = value.replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  };

  const mengeneinheitenOptions: IDropdownOption[] = useMemo(() => {
    return mengeneinheiten.map((me) => ({
      key: me.Kurzform || me.Einheit,
      text: `${me.Einheit} (${me.Kurzform})`,
    }));
  }, [mengeneinheiten]);

  const isAdminOrEinkauf = useMemo(() => {
    if (!permissions) return false;
    return permissions.isAdmin || permissions.isEinkauf;
  }, [permissions]);

  const handleDeleteSingle = async (artikel: IArtikel) => {
    if (!artikel.Id) return;

    try {
      setSaving(true);
      setError(null);
      await artikelApi.delete(artikel.Id);
      await loadArtikel();
      setDeleteDialogOpen(false);
      setSelectedArtikel(null);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen des Artikels');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setSaving(true);
      setError(null);

      for (const artikel of selectedItems) {
        if (artikel.Id) {
          await artikelApi.delete(artikel.Id);
        }
      }

      await loadArtikel();
      setBulkDeleteDialogOpen(false);
      setSelectedItems([]);
      setSelectionMode(false);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen der Artikel');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedArtikel?.Id) return;

    try {
      setSaving(true);
      setError(null);
      await artikelApi.update(selectedArtikel.Id, editFormData);
      await loadArtikel();
      setEditDialogOpen(false);
      setSelectedArtikel(null);
      setEditFormData({});
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern des Artikels');
    } finally {
      setSaving(false);
    }
  };

  const openViewDialog = (artikel: IArtikel) => {
    setSelectedArtikel(artikel);
    setViewDialogOpen(true);
  };

  const openEditDialog = async (artikel: IArtikel) => {
    setSelectedArtikel(artikel);
    setEditFormData({
      Artikelname: artikel.Artikelname,
      Artikelnummer: artikel.Artikelnummer,
      BCNummer: artikel.BCNummer,
      Artikelbeschreibung: artikel.Artikelbeschreibung,
      Artikelhyperlink: artikel.Artikelhyperlink,
      Einzelpreis: artikel.Einzelpreis,
      Mengeneinheit: artikel.Mengeneinheit,
    });
    setEinzelpreisInput(artikel.Einzelpreis?.toString().replace('.', ',') || '0');
    setEditDialogOpen(true);
    await loadMengeneinheiten();
  };

  const openDeleteDialog = (artikel: IArtikel) => {
    setSelectedArtikel(artikel);
    setDeleteDialogOpen(true);
  };

  const columns: IColumn[] = [
    {
      key: 'Artikelname',
      name: 'Artikelname',
      fieldName: 'Artikelname',
      minWidth: 200,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: 'Artikelnummer',
      name: 'Artikelnummer',
      fieldName: 'Artikelnummer',
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'BCNummer',
      name: 'Bögra-Nr.',
      fieldName: 'BCNummer',
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'actions',
      name: 'Aktionen',
      minWidth: 120,
      maxWidth: 120,
      onRender: (item: IArtikel) => (
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          <IconButton
            iconProps={{ iconName: 'View' }}
            title="Ansehen"
            onClick={() => openViewDialog(item)}
          />
          {isAdminOrEinkauf && (
            <>
              <IconButton
                iconProps={{ iconName: 'Edit' }}
                title="Bearbeiten"
                onClick={() => openEditDialog(item)}
              />
              <IconButton
                iconProps={{ iconName: 'Delete' }}
                title="Löschen"
                onClick={() => openDeleteDialog(item)}
              />
            </>
          )}
        </Stack>
      ),
    },
  ];

  const commandBarItems: ICommandBarItemProps[] = useMemo(() => {
    const items: ICommandBarItemProps[] = [];

    // "Neu" Button
    items.push({
      key: 'new',
      text: 'Neu',
      iconProps: { iconName: 'Add' },
      onClick: () => setCreateDialogOpen(true),
      disabled: !isAdminOrEinkauf,
    });

    // "Auswählen" Button
    items.push({
      key: 'select',
      text: selectionMode ? 'Auswahl beenden' : 'Auswählen',
      iconProps: { iconName: 'CheckboxComposite' },
      onClick: () => {
        setSelectionMode(!selectionMode);
        setSelectedItems([]);
      },
      disabled: !isAdminOrEinkauf,
    });

    // "Löschen" Button (nur wenn Elemente ausgewählt)
    if (selectionMode && selectedItems.length > 0 && isAdminOrEinkauf) {
      items.push({
        key: 'deleteSelected',
        text: `Löschen (${selectedItems.length})`,
        iconProps: { iconName: 'Delete' },
        onClick: () => setBulkDeleteDialogOpen(true),
      });
    }

    return items;
  }, [selectionMode, selectedItems, isAdminOrEinkauf]);

  const selection = useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          setSelectedItems(selection.getSelection() as IArtikel[]);
        },
      }),
    []
  );

  if (loading) {
    return (
      <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { height: '100vh' } }}>
        <Spinner label="Lade Artikel..." />
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: 20 } }}>
      <Text variant="xxLarge">Artikel</Text>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      <CommandBar items={commandBarItems} />

      <Stack horizontal tokens={{ childrenGap: 10 }}>
        <SearchBox
          placeholder="Artikel suchen (Name, Artikelnummer, Bögra-Nr.)..."
          value={searchText}
          onChange={(_, newValue) => setSearchText(newValue || '')}
          styles={{ root: { width: 400 } }}
        />
      </Stack>

      <DetailsList
        items={filteredArtikel}
        columns={columns}
        layoutMode={DetailsListLayoutMode.justified}
        selection={selection}
        selectionMode={selectionMode ? SelectionMode.multiple : SelectionMode.none}
        selectionPreservedOnEmptyClick
      />

      {/* View Dialog */}
      <Modal
        isOpen={viewDialogOpen}
        onDismiss={() => setViewDialogOpen(false)}
        styles={centeredModalStyles}
      >
        <div style={modalContentStyles.header}>
          <Text variant="xLarge">Artikel ansehen</Text>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => setViewDialogOpen(false)}
          />
        </div>
        <div style={modalContentStyles.body}>
          {selectedArtikel && (
            <Stack tokens={{ childrenGap: 10 }}>
              <TextField label="Artikelname" value={selectedArtikel.Artikelname} readOnly />
              <TextField label="Artikelnummer" value={selectedArtikel.Artikelnummer} readOnly />
              <TextField label="Bögra-Nr." value={selectedArtikel.BCNummer} readOnly />
              <TextField label="Beschreibung" value={selectedArtikel.Artikelbeschreibung} readOnly multiline rows={3} />
              <TextField label="Hyperlink" value={selectedArtikel.Artikelhyperlink} readOnly />
              <TextField label="Einzelpreis" value={selectedArtikel.Einzelpreis?.toString()} readOnly />
              <TextField label="Mengeneinheit" value={selectedArtikel.Mengeneinheit} readOnly />
            </Stack>
          )}
        </div>
        <div style={modalContentStyles.footer}>
          <PrimaryButton text="Schließen" onClick={() => setViewDialogOpen(false)} />
        </div>
      </Modal>

      {/* Edit Dialog */}
      <Modal
        isOpen={editDialogOpen}
        onDismiss={() => setEditDialogOpen(false)}
        styles={centeredModalStyles}
      >
        <div style={modalContentStyles.header}>
          <Text variant="xLarge">Artikel bearbeiten</Text>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => setEditDialogOpen(false)}
            disabled={saving}
          />
        </div>
        <div style={modalContentStyles.body}>
          <Stack tokens={{ childrenGap: 10 }}>
            <TextField
              label="Artikelname"
              value={editFormData.Artikelname || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Artikelname: value }))}
              required
            />
            <TextField
              label="Artikelnummer"
              value={editFormData.Artikelnummer || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Artikelnummer: value }))}
              required
            />
            <TextField
              label="Bögra-Nr."
              value={editFormData.BCNummer || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, BCNummer: value }))}
              required
            />
            <TextField
              label="Beschreibung"
              value={editFormData.Artikelbeschreibung || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Artikelbeschreibung: value }))}
              multiline
              rows={3}
            />
            <TextField
              label="Hyperlink"
              value={editFormData.Artikelhyperlink || ''}
              onChange={(_, value) => setEditFormData((prev) => ({ ...prev, Artikelhyperlink: value }))}
            />
            <TextField
              label="Einzelpreis (€)"
              type="text"
              placeholder="z.B. 12,50"
              value={einzelpreisInput}
              onChange={(_, value) => {
                setEinzelpreisInput(value || '');
                const preis = parseDecimal(value || '0');
                setEditFormData((prev) => ({ ...prev, Einzelpreis: preis }));
              }}
            />
            <Dropdown
              label="Mengeneinheit"
              placeholder="Mengeneinheit wählen"
              selectedKey={editFormData.Mengeneinheit || 'Stk'}
              onChange={(_, option) =>
                setEditFormData((prev) => ({ ...prev, Mengeneinheit: option?.key as string || 'Stk' }))
              }
              options={mengeneinheitenOptions}
            />
          </Stack>
        </div>
        <div style={modalContentStyles.footer}>
          <DefaultButton text="Abbrechen" onClick={() => setEditDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Speichern" onClick={handleSaveEdit} disabled={saving} />
        </div>
      </Modal>

      {/* Delete Dialog */}
      <Dialog
        hidden={!deleteDialogOpen}
        onDismiss={() => setDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Artikel löschen',
          subText: `Möchten Sie den Artikel "${selectedArtikel?.Artikelname}" (${selectedArtikel?.Artikelnummer}) wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
        }}
      >
        <DialogFooter>
          <DefaultButton text="Abbrechen" onClick={() => setDeleteDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Löschen" onClick={() => selectedArtikel && handleDeleteSingle(selectedArtikel)} disabled={saving} />
        </DialogFooter>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        hidden={!bulkDeleteDialogOpen}
        onDismiss={() => setBulkDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Artikel löschen',
          subText: `Möchten Sie ${selectedItems.length} Artikel wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
        }}
      >
        <DialogFooter>
          <DefaultButton text="Abbrechen" onClick={() => setBulkDeleteDialogOpen(false)} disabled={saving} />
          <PrimaryButton text="Löschen" onClick={handleBulkDelete} disabled={saving} />
        </DialogFooter>
      </Dialog>

      {/* Create Dialog */}
      <ArtikelAnlegenDialog
        isOpen={createDialogOpen}
        onDismiss={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          loadArtikel();
          setCreateDialogOpen(false);
        }}
      />
    </Stack>
  );
};
